import { Avatar, Badge, SxProps, Theme, Tooltip } from "@mui/material";
import { ReactNode } from "react";
import { GREEN } from "@/config/mui/colorPalette";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import { getAvatarColor, getUserCalendarColor } from "@/utils";
import { getInitials } from "@/utils/names";

export type AvatarInitialsValueType = {
  firstName: string;
  lastName: string;
  id?: string;
  userIndex?: number;
  color?: string;
  initials?: string;
};

export type InitialsAvatarProps = {
  initials: AvatarInitialsValueType;
  size?: number;
  sx?: SxProps<Theme>;
  inputChildren?: ReactNode;
  badgeContent?: ReactNode;
  onClick?: () => void;
};

function InitialsAvatar({
  initials,
  size = 48,
  sx = {},
  inputChildren,
  badgeContent,
  onClick,
}: InitialsAvatarProps) {
  const { firstName, lastName } = initials;

  const { providerColorsConfigEnabled } = useFeatureFlags();
  const { colorObjectByIndex, colorObjectConfigured } =
    getUserCalendarColor(initials);

  const userColorPalette =
    providerColorsConfigEnabled && colorObjectConfigured
      ? colorObjectConfigured
      : initials.userIndex !== undefined && colorObjectByIndex;

  const textColor = userColorPalette ? userColorPalette[100] : GREEN[100];

  const backgroundColor = userColorPalette
    ? providerColorsConfigEnabled
      ? userColorPalette[40]
      : userColorPalette[60]
    : getAvatarColor(initials);

  return (
    <Tooltip title={!inputChildren ? `${firstName} ${lastName}` : ""} arrow>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClick={onClick}
        badgeContent={badgeContent}
        sx={{
          cursor: onClick ? "pointer" : "default",
          pointerEvents: onClick ? "auto" : "none",
        }}
      >
        <Avatar
          alt={`${firstName} ${lastName}`}
          sx={{
            width: size,
            height: size,
            color: textColor,
            typography: "subtitleSmall",
            background: backgroundColor,
            pointerEvents: "auto",
            ...sx,
          }}
        >
          {inputChildren ||
            (providerColorsConfigEnabled && initials.initials) ||
            getInitials(firstName, lastName)}
        </Avatar>
      </Badge>
    </Tooltip>
  );
}

export default InitialsAvatar;
